import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { PartnerService } from '@/services/partnerService';
import { 
  Handshake, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  ArrowLeft,
  Globe,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Ban,
  Power
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Partner {
  id: number;
  name: string;
  logo: string;
  address: string;
  website: string;
  email: string;
  phone: string;
  description: string;
  social_media?: string;
  is_active: boolean;
  status?: 'pendente' | 'aprovado' | 'rejeitado';
  created_at: string;
  updated_at: string;
}

const AdminPartners = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado para o input
  const [filterStatus, setFilterStatus] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Verificar se é admin (AdminLayout já faz isso)

  useEffect(() => {
    loadPartners();
  }, [currentPage, itemsPerPage, searchTerm, filterStatus]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      
      // Buscar dados reais do banco
      const allPartners = await PartnerService.getAllPartnersForAdmin();

      // Aplicar filtros
      let filteredPartners = allPartners;
      
      // Filtro por termo de busca
      if (searchTerm) {
        filteredPartners = filteredPartners.filter(partner => 
          partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (partner.email && partner.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (partner.website && partner.website.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Filtro por status
      if (filterStatus !== 'todos') {
        filteredPartners = filteredPartners.filter(partner => {
          if (filterStatus === 'pendente') {
            return partner.status === 'pendente';
          } else if (filterStatus === 'aprovado') {
            return partner.status === 'aprovado';
          } else if (filterStatus === 'rejeitado') {
            return partner.status === 'rejeitado';
          } else if (filterStatus === 'ativo') {
            return Boolean(partner.is_active) === true;
          } else if (filterStatus === 'inativo') {
            return Boolean(partner.is_active) === false;
          }
          return true;
        });
      }

      // Calcular paginação
      const total = filteredPartners.length;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedPartners = filteredPartners.slice(startIndex, endIndex);

      setPartners(paginatedPartners);
      setTotalItems(total);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleEditPartner = (partnerId: number) => {
    navigate(`/admin/parceiros/editar/${partnerId}`);
  };

  const handleTogglePartnerStatus = async (partnerId: number) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;
    
    const action = partner.is_active ? 'desativar' : 'ativar';
    if (confirm(`Tem certeza que deseja ${action} este parceiro?`)) {
      try {
        console.log(`${action.charAt(0).toUpperCase() + action.slice(1)} parceiro:`, partnerId);
        await PartnerService.togglePartnerStatus(partnerId);
        loadPartners();
      } catch (error) {
        console.error(`Erro ao ${action} parceiro:`, error);
      }
    }
  };

  const handleApprovePartner = async (partnerId: number) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;
    
    if (confirm(`Tem certeza que deseja aprovar o parceiro "${partner.name}"?`)) {
      try {
        await PartnerService.updatePartnerApproval(partnerId, 'aprovado');
        loadPartners();
      } catch (error) {
        console.error('Erro ao aprovar parceiro:', error);
        alert('Erro ao aprovar parceiro. Tente novamente.');
      }
    }
  };

  const handleRejectPartner = async (partnerId: number) => {
    const partner = partners.find(p => p.id === partnerId);
    if (!partner) return;
    
    if (confirm(`Tem certeza que deseja rejeitar o parceiro "${partner.name}"?`)) {
      try {
        await PartnerService.updatePartnerApproval(partnerId, 'rejeitado');
        loadPartners();
      } catch (error) {
        console.error('Erro ao rejeitar parceiro:', error);
        alert('Erro ao rejeitar parceiro. Tente novamente.');
      }
    }
  };

  const handleViewPartner = (partnerId: number) => {
    navigate(`/parceiro/${partnerId}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando parceiros...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Gerenciar Parceiros</h1>
            <p className="text-muted-foreground">
              Cadastre, edite e gerencie parceiros do sistema
            </p>
    </div>
    </div>
        <Button onClick={() => navigate('/admin/parceiros/novo')}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Parceiro
        </Button>
    </div>

      {/* Busca */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar por nome, email ou website..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-10"
                  />
                </div>
                <Button 
                  onClick={handleSearch}
                  variant="outline"
                  className="px-4"
                >
                  <Search className="h-4 w-4" />
                </Button>
                {searchTerm && (
                  <Button 
                    onClick={handleClearSearch}
                    variant="outline"
                    className="px-4"
                  >
                    Limpar
                  </Button>
                )}
              </div>
              {searchTerm && (
                <p className="text-sm text-muted-foreground mt-2">
                  Buscando por: <strong>"{searchTerm}"</strong>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select value={filterStatus} onValueChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(1);
              }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="aprovado">Aprovado</SelectItem>
                  <SelectItem value="rejeitado">Rejeitado</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
    </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Itens por página:</span>
              <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
    </div>
    </div>
        </CardContent>
      </Card>

      {/* Tabela de Parceiros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5" />
            Parceiros ({totalItems} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parceiro</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Website</TableHead>
                <TableHead>Aprovação</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data de Cadastro</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Handshake className="h-4 w-4 text-primary" />
    </div>
                      <div>
                        <div className="font-medium">{partner.name}</div>
    </div>
    </div>
                  </TableCell>
                  <TableCell>{partner.email || '-'}</TableCell>
                  <TableCell>{partner.phone || '-'}</TableCell>
                  <TableCell>
                    {partner.website ? (
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Globe className="h-4 w-4" />
                        <a 
                          href={partner.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Visitar
                        </a>
    </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        partner.status === 'aprovado' ? 'default' :
                        partner.status === 'pendente' ? 'secondary' :
                        'destructive'
                      }
                    >
                      {partner.status === 'aprovado' ? 'Aprovado' :
                       partner.status === 'pendente' ? 'Pendente' :
                       partner.status === 'rejeitado' ? 'Rejeitado' :
                       'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={partner.is_active ? 'default' : 'secondary'}>
                      {partner.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(partner.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewPartner(partner.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditPartner(partner.id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        {partner.status !== 'aprovado' && (
                          <DropdownMenuItem 
                            onClick={() => handleApprovePartner(partner.id)}
                            className="text-green-600"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Aprovar
                          </DropdownMenuItem>
                        )}
                        {partner.status !== 'aprovado' && partner.status !== 'rejeitado' && (
                          <DropdownMenuItem 
                            onClick={() => handleRejectPartner(partner.id)}
                            className="text-red-600"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Reprovar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleTogglePartnerStatus(partner.id)}
                          className={partner.is_active ? "text-red-600" : "text-green-600"}
                        >
                          {partner.is_active ? (
                            <>
                              <Power className="h-4 w-4 mr-2" />
                              Desativar
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Ativar
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registros
    </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
    </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
    </div>
    </div>
          </CardContent>
        </Card>
      )}
    </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPartners;
